/**
 * Utilities for working with offset map and connectivity graphs
 * 
 * @see types/offsetMap.ts for type definitions
 */

import type { OffsetMap, ManifestFrameOffset } from '../types/offsetMap'
import type { Manifest } from './calculateOffsetMapFromManifest'

/**
 * Build connectivity graph from offset map
 * 
 * Priority order (NEW - all_pairs FIRST):
 * 1. Use all_pairs (PRIMARY - contains all frame pairs with ≥10 shared points)
 * 2. Use precomputed connectivity (direct adjacency only)
 * 3. Use precomputed transitiveConnectivity (full reachability)
 * 4. Use sequential offsets only (fallback)
 * 
 * @param offsetMap - The offset map containing connectivity data
 * @param expectedFrameCount - Expected number of frames (for validation)
 * @returns Map from frame index to set of reachable frame indices
 */
export function buildConnectivityGraph(
  offsetMap: OffsetMap | null,
  expectedFrameCount?: number
): Map<number, Set<number>> {
  const graph = new Map<number, Set<number>>()
  
  if (!offsetMap) {
    console.warn(`[buildConnectivityGraph] offsetMap is null`)
    return graph
  }
  
  // **PRIORITY 1: Use all_pairs as PRIMARY data source**
  // all_pairs contains all frame pairs with ≥10 shared 3D points (2,847 edges in our datasets)
  if (offsetMap.all_pairs && Object.keys(offsetMap.all_pairs).length > 0) {
    for (const key of Object.keys(offsetMap.all_pairs)) {
      const [fromStr, toStr] = key.split('_')
      const from = parseInt(fromStr)
      const to = parseInt(toStr)
      
      if (isNaN(from) || isNaN(to)) {
        continue
      }
      
      if (!graph.has(from)) {
        graph.set(from, new Set())
      }
      // Add bidirectional edges
      graph.get(from)!.add(to)
      
      if (!graph.has(to)) {
        graph.set(to, new Set())
      }
      graph.get(to)!.add(from)
    }
    
    const edgeCount = Array.from(graph.values()).reduce((sum, s) => sum + s.size, 0)
    console.log(`[buildConnectivityGraph] ✅ all_pairs: ${graph.size} frames, ${edgeCount} edges`)
    return graph
  }
  
  // Priority 2: Use precomputed connectivity (direct adjacency only - SPARSE neighbors)
  if (offsetMap.connectivity && Object.keys(offsetMap.connectivity).length > 0) {
    let loadedCount = 0
    for (const [frameStr, adjacent] of Object.entries(offsetMap.connectivity)) {
      const frame = parseInt(frameStr)
      if (isNaN(frame)) {
        continue
      }
      if (!graph.has(frame)) {
        graph.set(frame, new Set())
      }
      adjacent.forEach(adj => {
        graph.get(frame)!.add(adj)
      })
      loadedCount++
    }
    
    if (loadedCount > 0) {
      console.log(`[buildConnectivityGraph] Using connectivity (${loadedCount} frames)`)
      return graph
    }
  }
  
  // Priority 3: Use precomputed transitive connectivity (full reachability fallback)
  if (offsetMap.transitiveConnectivity && Object.keys(offsetMap.transitiveConnectivity).length > 0) {
    let loadedCount = 0
    for (const [frameStr, reachable] of Object.entries(offsetMap.transitiveConnectivity)) {
      const frame = parseInt(frameStr)
      if (isNaN(frame)) {
        continue
      }
      graph.set(frame, new Set(reachable))
      loadedCount++
    }
    
    if (loadedCount > 0) {
      console.log(`[buildConnectivityGraph] Using transitiveConnectivity (${loadedCount} frames)`)
      return graph
    }
  }
  
  // Priority 4: Build from sequential offsets (legacy fallback)
  if (offsetMap.sequential && Object.keys(offsetMap.sequential).length > 0) {
    for (const key of Object.keys(offsetMap.sequential)) {
      const [fromStr, toStr] = key.split('_')
      const from = parseInt(fromStr)
      const to = parseInt(toStr)
      
      if (isNaN(from) || isNaN(to)) {
        continue
      }
      
      if (!graph.has(from)) {
        graph.set(from, new Set())
      }
      graph.get(from)!.add(to)
    }
    
    if (graph.size > 0) {
      console.log(`[buildConnectivityGraph] Using sequential (${graph.size} frames)`)
      return graph
    }
  }
  
  console.error(`[buildConnectivityGraph] ❌ No connectivity data found`)
}

/**
 * Check if frame B is reachable from frame A
 * 
 * @param frameA - Source frame index
 * @param frameB - Target frame index
 * @param connectivityGraph - Connectivity graph from buildConnectivityGraph()
 * @returns true if frameB is reachable from frameA, false otherwise
 */
export function isFrameConnected(
  frameA: number,
  frameB: number,
  connectivityGraph: Map<number, Set<number>>
): boolean {
  const reachable = connectivityGraph.get(frameA)
  return reachable ? reachable.has(frameB) : false
}

/**
 * Decompress distance_3d from manifest compressed format
 * Manifest stores distance as uint16 scaled by 64x (precision: 0.015 pixels)
 * 
 * @param compressedDist - Compressed distance (uint16)
 * @returns Decompressed distance in pixels
 */
export function decompressDistance(compressedDist: number): number {
  return compressedDist / 64
}

/**
 * Build connectivity graph directly from manifest.json
 * 
 * Manifest stores offsets per-frame: manifest.frames[N].offsets = {neighborId: {dx, dy, dist}, ...}
 * This extracts the connectivity structure (which frames are neighbors of which)
 * 
 * @param manifest - Manifest containing frame offsets
 * @returns Map from frame ID to set of neighbor frame IDs
 */
export function buildConnectivityGraphFromManifest(
  manifest: Manifest | null
): Map<number, Set<number>> {
  const graph = new Map<number, Set<number>>()
  
  if (!manifest || !manifest.frames || manifest.frames.length === 0) {
    console.warn('[buildConnectivityGraphFromManifest] No manifest data')
    return graph
  }

  // OPTIMIZED: Use neighbors array (bidirectional) instead of scanning offsets
  // Each frame has a neighbors list that includes all connected frames
  manifest.frames.forEach(frame => {
    const frameId = frame.id
    
    if (!graph.has(frameId)) {
      graph.set(frameId, new Set())
    }
    
    // Use the neighbors array if available (NEW optimization)
    if (frame.neighbors && Array.isArray(frame.neighbors)) {
      frame.neighbors.forEach(neighborId => {
        graph.get(frameId)!.add(neighborId)
      })
    } else if (frame.offsets && typeof frame.offsets === 'object') {
      // Fallback: extract from offsets keys (for old manifests without neighbors field)
      Object.keys(frame.offsets).forEach(neighborIdStr => {
        const neighborId = parseInt(neighborIdStr, 10)
        if (!isNaN(neighborId)) {
          graph.get(frameId)!.add(neighborId)
        }
      })
    }
  })
  
  const edgeCount = Array.from(graph.values()).reduce((sum, s) => sum + s.size, 0)
  console.log(`[buildConnectivityGraphFromManifest] ✅ ${graph.size} frames, ${edgeCount} edges from manifest`)
  
  return graph
}

/**
 * Build connectivity graph from section_3d data
 * 
 * Unified section_3d.json format stores points with observations.
 * Each point lists cameras that observe it: points[].cameras = [[frame_id, px, py], ...]
 * We build connectivity by connecting all camera pairs that share observations.
 * 
 * @param section3d - Section3DData containing points with camera observations
 * @returns Map from frame ID to set of neighbor frame IDs
 */
export function buildConnectivityGraphFromSection3D(
  section3d: any // Section3DData
): Map<number, Set<number>> {
  const graph = new Map<number, Set<number>>()
  
  if (!section3d || !section3d.points || section3d.points.length === 0) {
    console.warn('[buildConnectivityGraphFromSection3D] No section3d data')
    return graph
  }

  // Initialize all cameras
  if (section3d.cameras && Array.isArray(section3d.cameras)) {
    section3d.cameras.forEach((cam: any) => {
      const frameId = typeof cam.id === 'string' ? parseInt(cam.id, 10) : cam.id
      if (!isNaN(frameId)) {
        graph.set(frameId, new Set())
      }
    })
  }

  // Iterate through all points and connect camera pairs that observe them
  let totalConnections = 0
  section3d.points.forEach((point: any) => {
    if (!point.cameras || !Array.isArray(point.cameras) || point.cameras.length < 2) {
      return // Skip points with <2 observations
    }

    // Extract camera IDs from observations: [frame_id, pixel_x, pixel_y]
    const cameraIds = point.cameras.map((obs: any[]) => obs[0])

    // Connect all pairs of cameras observing this point (bidirectional)
    for (let i = 0; i < cameraIds.length; i++) {
      for (let j = i + 1; j < cameraIds.length; j++) {
        const camA = cameraIds[i]
        const camB = cameraIds[j]

        // Ensure both cameras have entries
        if (!graph.has(camA)) {
          graph.set(camA, new Set())
        }
        if (!graph.has(camB)) {
          graph.set(camB, new Set())
        }

        // Add bidirectional edges
        graph.get(camA)!.add(camB)
        graph.get(camB)!.add(camA)
        totalConnections += 2
      }
    }
  })

  const edgeCount = Array.from(graph.values()).reduce((sum, s) => sum + s.size, 0)
  console.log(`[buildConnectivityGraphFromSection3D] ✅ ${graph.size} frames, ${edgeCount} edges from section_3d observations`)
  
  return graph
}

/**
 * Sort manifest offsets by distance (using compressed distance field)
 * Returns closest neighbors first for efficient context loading
 * 
 * @param offsets - Manifest offsets dict with dist field
 * @returns Sorted array of [frameIdStr, offset] pairs, closest first
 */
export function sortOffsetsByDistance(
  offsets: Record<string, ManifestFrameOffset>
): Array<[string, ManifestFrameOffset]> {
  return Object.entries(offsets)
    .sort(([, a], [, b]) => {
      // Direct integer comparison (no sqrt needed!)
      return a.dist - b.dist
    })
}

/**
 * Get closest neighbor frames from manifest offsets
 * Useful for context loading during pan gestures
 * 
 * @param offsets - Manifest offsets dict with dist field
 * @param maxCount - Maximum number of neighbors to return (default: 3)
 * @returns Sorted array of neighbors with decompressed distances
 */
export function getClosestNeighbors(
  offsets: Record<string, ManifestFrameOffset>,
  maxCount: number = 3
): Array<{frameId: number, offset: ManifestFrameOffset, distance: number}> {
  return Object.entries(offsets)
    .map(([frameIdStr, offset]) => ({
      frameId: parseInt(frameIdStr, 10),
      offset,
      distance: decompressDistance(offset.dist)
    }))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, maxCount)
}

/**
 * Calculate angular difference between two directions in degrees
 * Normalizes to 0-180° range (shortest angular distance)
 * 
 * @param offsetDx - Horizontal offset (pixels)
 * @param offsetDy - Vertical offset (pixels)
 * @param panAngleDegrees - Reference pan direction in degrees (default: 0)
 * @returns Angular difference in degrees (0-180)
 */
export function calculateAngleDiff(
  offsetDx: number,
  offsetDy: number,
  panAngleDegrees: number = 0
): number {
  // Calculate angle of offset vector in degrees
  const offsetAngle = Math.atan2(offsetDy, offsetDx) * (180 / Math.PI)
  
  // Normalize angles to 0-360
  const normalizedPan = ((panAngleDegrees % 360) + 360) % 360
  const normalizedOffset = ((offsetAngle % 360) + 360) % 360
  
  // Calculate shortest angular distance
  let diff = Math.abs(normalizedOffset - normalizedPan)
  if (diff > 180) {
    diff = 360 - diff
  }
  
  return diff
}

/**
 * Build a synthetic OffsetMap from manifest.json
 * 
 * Creates offsetMap structure containing:
 * - connectivity: Frame adjacency graph from neighbors array
 * - all_pairs: Offset data with distance_3d and angle_diff pre-computed
 * - metadata: Minimal stub for React dependency tracking
 * 
 * This allows tap indicator and other features to work WITHOUT loading offset_map.json,
 * saving 2-3MB per section while maintaining identical functionality.
 * 
 * @param manifest - Manifest data containing frames with offsets and neighbors
 * @returns OffsetMap structure suitable for all downstream consumers, or null if invalid
 */
export function buildSyntheticOffsetMapFromManifest(manifest: Manifest | any | null): OffsetMap | null {
  if (!manifest || !manifest.frames || manifest.frames.length === 0) {
    console.warn('[buildSyntheticOffsetMapFromManifest] No manifest data, returning null')
    return null
  }

  console.log(`[buildSyntheticOffsetMapFromManifest] 🔄 Building synthetic offsetMap from ${manifest.frames.length} frames...`)
  const startTime = performance.now()

  // Step 1: Build connectivity graph from neighbors array or offsets keys
  const connectivity: Record<string, number[]> = {}
  manifest.frames.forEach((frame: any) => {
    const frameId = frame.id
    
    if (!connectivity[frameId]) {
      connectivity[frameId] = []
    }
    
    // Use neighbors array if available (primary source) - new manifest format
    if (frame.neighbors && Array.isArray(frame.neighbors)) {
      connectivity[frameId] = [...frame.neighbors]
    } else if (frame.offsets && typeof frame.offsets === 'object') {
      // Fallback: extract from offsets keys (old manifest format without neighbors field)
      connectivity[frameId] = Object.keys(frame.offsets)
        .map((key: string) => parseInt(key, 10))
        .filter((id: number) => !isNaN(id))
    }
  })

  // Step 2: Build all_pairs with dx, dy, distance_3d, angle_diff
  const all_pairs: Record<string, any> = {}
  const frameMap = new Map(manifest.frames.map((f: any) => [f.id, f]))

  frameMap.forEach((frame: any) => {
    if (!frame.offsets || typeof frame.offsets !== 'object') {
      return
    }
    
    Object.entries(frame.offsets).forEach(([neighborIdStr, offset]: [string, any]) => {
      const neighborId = parseInt(neighborIdStr, 10)
      if (isNaN(neighborId)) {
        return
      }
      
      // Forward offset (as stored in manifest)
      const forwardKey = `${frame.id}_${neighborId}`
      
      // Decompress distance_3d from manifest's compressed dist field
      const distance3d = offset.dist ? offset.dist / 64 : Math.sqrt(offset.dx ** 2 + offset.dy ** 2)
      
      // Pre-compute angle_diff from dx/dy for NewModeViewer angle filtering
      const angleDiff = Math.abs(Math.atan2(offset.dy, offset.dx) * (180 / Math.PI))
      
      all_pairs[forwardKey] = {
        dx: offset.dx,
        dy: offset.dy,
        dz: 0,  // Manifest doesn't contain scale factor (can compute if needed)
        distance_3d: distance3d,
        confidence: 0.8,  // Default confidence for synthetic data
        pointCount: 0,  // Not available in manifest
        angle_diff: angleDiff,  // Pre-computed for NewModeViewer
        yaw_diff: undefined,
        pitch_diff: undefined,
        roll_diff: undefined,
        similar_orientation: undefined,
        motion_type: 'unknown'
      }
      
      // Reverse offset (generated by negating dx/dy for bidirectional navigation)
      const reverseKey = `${neighborId}_${frame.id}`
      if (!all_pairs[reverseKey]) {
        all_pairs[reverseKey] = {
          dx: -offset.dx,
          dy: -offset.dy,
          dz: 0,
          distance_3d: distance3d,  // Distance unchanged for reverse direction
          confidence: 0.8,
          pointCount: 0,
          angle_diff: (angleDiff + 180) % 360,  // Reverse angle
          yaw_diff: undefined,
          pitch_diff: undefined,
          roll_diff: undefined,
          similar_orientation: undefined,
          motion_type: 'unknown'
        }
      }
    })
  })

  // Step 3: Build metadata stub
  const metadata = {
    frameCount: manifest.frames.length,
    sceneCenter: [0, 0, 0] as [number, number, number],
    generated: new Date().toISOString(),
    version: 'synthetic-from-manifest'
  }

  const offsetMap: OffsetMap = {
    metadata,
    all_pairs,
    connectivity,
    // Optional fields not included in synthetic version:
    // sequential, transitiveConnectivity, components, extended_pairs, extended_connectivity
  }

  const elapsed = performance.now() - startTime
  console.log(`[buildSyntheticOffsetMapFromManifest] ✅ Built synthetic offsetMap in ${elapsed.toFixed(2)}ms`)
  console.log(`[buildSyntheticOffsetMapFromManifest]    ${Object.keys(connectivity).length} frames, ${Object.keys(all_pairs).length} offset pairs`)

  return offsetMap
}